import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod';

import { FormField } from '@/components/shared/form-field';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { PhoneInput } from '@/components/ui/phone-input';
import { Stepper } from '@/components/ui/stepper';
import { useRegister } from '@/hooks/use-auth';
import { captureReferralCodeFromSearch } from '@/lib/referral-link';
import { useAuthStore } from '@/stores/auth-store';
import { usePreferences } from '@/stores/preferences-store';

import { AuthLayout } from './auth-layout';
import { FormError } from './form-error';
import { messageFor } from './validation-message';

const schema = z
  .object({
    firstName: z.string().trim().min(1, 'firstNameRequired'),
    lastName: z.string().trim().min(1, 'lastNameRequired'),
    email: z.string().min(1, 'emailRequired').pipe(z.email('emailInvalid')),
    // Matches the server's rule so the person is told before the round trip, not after.
    password: z.string().min(12, 'passwordTooShort'),
    phone: z.string().optional(),
    acceptTerms: z.boolean(),
  })
  .refine((values) => values.acceptTerms, {
    message: 'termsRequired',
    path: ['acceptTerms'],
  });

type Values = z.infer<typeof schema>;

type Step = 0 | 1 | 2;

const STEP_FIELDS: Record<Step, (keyof Values)[]> = {
  0: ['firstName', 'lastName'],
  1: ['email', 'password'],
  2: ['phone', 'acceptTerms'],
};

export const RegisterPage = () => {
  const { t } = useTranslation('auth');
  const { t: tCommon } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const inviteToken = params.get('invite') ?? undefined;
  const invitedEmail = params.get('email') ?? '';
  const status = useAuthStore((state) => state.status);
  const { locale, timezone } = usePreferences();
  const register = useRegister();
  const [step, setStep] = useState<Step>(0);

  useEffect(() => {
    captureReferralCodeFromSearch(params);
  }, [params]);

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: invitedEmail,
      password: '',
      phone: '',
      acceptTerms: false,
    },
  });

  if (status === 'authenticated' && !inviteToken) return <Navigate to="/home" replace />;
  if (status === 'authenticated' && inviteToken) {
    return <Navigate to={`/accept-invite?token=${encodeURIComponent(inviteToken)}`} replace />;
  }

  const steps = [
    { id: 'name', label: t('register.steps.name') },
    { id: 'account', label: t('register.steps.account') },
    { id: 'phone', label: t('register.steps.phone') },
  ];

  const stepDescriptions = [
    t('register.stepDescription.name'),
    t('register.stepDescription.account'),
    t('register.stepDescription.phone'),
  ] as const;

  const goNext = async () => {
    const valid = await form.trigger(STEP_FIELDS[step]);
    if (!valid) return;
    setStep((current) => (current < 2 ? ((current + 1) as Step) : current));
  };

  const goBack = () => {
    setStep((current) => (current > 0 ? ((current - 1) as Step) : current));
  };

  const submit = form.handleSubmit(async (values) => {
    try {
      await register.mutateAsync({
        email: values.email,
        password: values.password,
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone || undefined,
        locale,
        timezone,
        inviteToken,
        acceptedTerms: true,
      });
      void navigate('/churches/select', { replace: true });
    } catch {
      // Shown by the mutation's error state.
    }
  });

  return (
    <AuthLayout
      title={t('register.title')}
      description={stepDescriptions[step]}
      cardClassName="max-w-lg"
      footer={
        <div className="flex flex-col items-center gap-2 text-center">
          <p>
            {t('register.haveAccount')}{' '}
            <Link to="/sign-in" className="text-brand hover:underline">
              {t('register.signIn')}
            </Link>
          </p>
          <Link to="/" className="text-brand hover:underline">
            {t('backToHome')}
          </Link>
        </div>
      }
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (step < 2) {
            void goNext();
            return;
          }
          void submit();
        }}
        className="space-y-5"
        noValidate
      >
        <Stepper steps={steps} current={step} label={t('register.stepperLabel')} />

        {step === 0 ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              label={t('field.firstName')}
              required
              error={messageFor(form.formState.errors.firstName?.message, t)}
            >
              {(field) => (
                <Input
                  {...field}
                  {...form.register('firstName')}
                  autoComplete="given-name"
                  autoFocus
                />
              )}
            </FormField>

            <FormField
              label={t('field.lastName')}
              required
              error={messageFor(form.formState.errors.lastName?.message, t)}
            >
              {(field) => (
                <Input {...field} {...form.register('lastName')} autoComplete="family-name" />
              )}
            </FormField>
          </div>
        ) : null}

        {step === 1 ? (
          <div className="space-y-4">
            <FormField
              label={t('field.email')}
              required
              error={messageFor(form.formState.errors.email?.message, t)}
            >
              {(field) => (
                <Input
                  {...field}
                  {...form.register('email')}
                  type="email"
                  autoComplete="email"
                  autoFocus
                  readOnly={Boolean(inviteToken)}
                />
              )}
            </FormField>

            <FormField
              label={t('field.password')}
              required
              tipId="auth.password"
              error={messageFor(form.formState.errors.password?.message, t)}
            >
                {(field) => (
                  <PasswordInput
                    {...field}
                    {...form.register('password')}
                    autoComplete="new-password"
                  />
                )}
            </FormField>
          </div>
        ) : null}

        {step === 2 ? (
          <div className="space-y-4">
            <FormField
              label={t('field.phone')}
              tipId="auth.phone"
              optionalLabel={tCommon('field.optional')}
              error={messageFor(form.formState.errors.phone?.message, t)}
            >
              {(field) => (
                <Controller
                  control={form.control}
                  name="phone"
                  render={({ field: control }) => (
                    <PhoneInput
                      id={field.id}
                      aria-describedby={field['aria-describedby']}
                      invalid={field.invalid}
                      value={control.value ?? null}
                      onChange={(value) => control.onChange(value ?? '')}
                    />
                  )}
                />
              )}
            </FormField>

            <Controller
              control={form.control}
              name="acceptTerms"
              render={({ field }) => (
                <div className="space-y-2">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="register-accept-terms"
                      checked={field.value}
                      onCheckedChange={(checked) => field.onChange(checked === true)}
                      aria-invalid={Boolean(form.formState.errors.acceptTerms)}
                      className="mt-0.5"
                    />
                    <Label htmlFor="register-accept-terms" className="font-normal leading-relaxed text-fg-secondary">
                      {t('register.terms.prefix')}{' '}
                      <Link
                        to="/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={t('register.terms.termsAria')}
                        className="text-brand hover:underline"
                      >
                        {t('register.terms.termsLink')}
                      </Link>{' '}
                      {t('register.terms.and')}{' '}
                      <Link
                        to="/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={t('register.terms.privacyAria')}
                        className="text-brand hover:underline"
                      >
                        {t('register.terms.privacyLink')}
                      </Link>
                      .
                    </Label>
                  </div>
                  {messageFor(form.formState.errors.acceptTerms?.message, t) ? (
                    <p className="text-body-sm text-danger" role="alert">
                      {messageFor(form.formState.errors.acceptTerms?.message, t)}
                    </p>
                  ) : null}
                </div>
              )}
            />
          </div>
        ) : null}

        <FormError error={register.error} />

        <div className="flex justify-end gap-2">
          {step > 0 ? (
            <Button type="button" variant="outline" onClick={goBack}>
              {tCommon('action.back')}
            </Button>
          ) : null}
          {step < 2 ? (
            <Button type="submit">{tCommon('action.continue')}</Button>
          ) : (
            <Button type="submit" loading={form.formState.isSubmitting}>
              {t('register.submit')}
            </Button>
          )}
        </div>
      </form>
    </AuthLayout>
  );
};
